import type { SessionUser } from "@/types/clientContext";
import { create } from "zustand";
import { createJSONStorage, persist } from "zustand/middleware";

type ClientContextState = {
  currSessionUserId: string;
  myShopifyDomain: string;
  ownerEmail: string;
  planName: string;
  aiod_plan?: string;
  freeTrialStartedAt?: Date;
  /** ISO timestamp of first ShopData row sync — used for funnel analytics (seconds since install). */
  appFirstSyncedAt: string | null;
  sessionUsers: SessionUser[];
  isEarlyUser?: boolean;
  setShopData: (data: {
    myShopifyDomain: string;
    ownerEmail: string;
    planName: string;
    aiod_plan: string;
    freeTrialStartedAt: Date;
  }) => void;
  setAppFirstSyncedAt: (iso: string | null) => void;
  setMyShopifyDomain: (myShopifyDomain: string) => void;
  setIsEarlyUser: (isEarlyUser: boolean) => void;
  setSessionUsers: (sessionUsers: SessionUser[]) => void;
  setCurrSessionUserId: (currSessionUserId: string) => void;
  hasLoaded: boolean;
  setHasLoaded: (hasLoaded: boolean) => void;
};

/**
 * Shared browser-side shop and user context. Support chat, analytics, and other
 * client integrations can consume it without coupling the data to one vendor.
 */
export const useShopDataStore = create<ClientContextState>()(
  persist(
    (set) => ({
      currSessionUserId: "",
      myShopifyDomain: "",
      ownerEmail: "",
      planName: "",
      appFirstSyncedAt: null,
      sessionUsers: [],
      setShopData: (data) =>
        set({
          myShopifyDomain: data.myShopifyDomain,
          ownerEmail: data.ownerEmail,
          planName: data.planName,
          aiod_plan: data.aiod_plan,
          freeTrialStartedAt: data.freeTrialStartedAt || undefined,
        }),
      setAppFirstSyncedAt: (iso) => set({ appFirstSyncedAt: iso }),
      setMyShopifyDomain: (myShopifyDomain) => set({ myShopifyDomain }),
      setIsEarlyUser: (isEarlyUser) => set({ isEarlyUser }),
      setSessionUsers: (sessionUsers) => set({ sessionUsers }),
      setCurrSessionUserId: (currSessionUserId) => set({ currSessionUserId }),
      hasLoaded: false,
      setHasLoaded: (hasLoaded) => set({ hasLoaded }),
    }),
    {
      // Keep the existing key so current installations retain their cached context.
      name: "aiod-shop-data-storage",
      storage: createJSONStorage(() => localStorage),
      skipHydration: true,
      onRehydrateStorage: () => (state) => {
        state?.setHasLoaded(true);
      },
    },
  ),
);
